import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Typography,
} from '@material-ui/core';
import { roleLabel } from '../../pages/user-management/user.helper';

interface UserScopeModalProps {
    open: boolean;
    user: any;
    stores: Array<{
        storeId: string;
        storeName?: string;
        city?: string;
        zone?: string;
    }>;
    zones: string[];
    onClose: () => void;
    onSave: (
        userId: string | number,
        addScopeValues: string[],
        removeScopeValues: string[],
    ) => void;
}

/**
 * Edits a user's scope (PRD UM-5 / UM-6).
 *
 * <p>Sends only the delta, not the whole set — the backend flips is_active on the existing row so
 * a re-granted value keeps its audit history, and it computes before/after from that same row.
 *
 * <p>The options passed in are already narrowed server-side to what the editor may grant, so there
 * is no filtering by the editor's own zones here.
 */
const UserScopeModal: React.FC<UserScopeModalProps> = ({
    open,
    user,
    stores,
    zones,
    onClose,
    onSave,
}) => {
    const isZoneScoped = user?.scopeType === 'ZONE';
    const initial: string[] = useMemo(() => user?.scopeValues ?? [], [user]);

    const [selected, setSelected] = useState<string[]>(initial);
    const [filterText, setFilterText] = useState('');

    const options = useMemo(() => {
        if (isZoneScoped)
            return (zones ?? []).map((z) => ({ value: z, label: z }));
        const text = filterText.trim().toLowerCase();
        return (stores ?? [])
            .filter(
                (s) =>
                    !text ||
                    s.storeId?.toLowerCase().includes(text) ||
                    s.storeName?.toLowerCase().includes(text) ||
                    s.city?.toLowerCase().includes(text),
            )
            .map((s) => ({
                value: s.storeId,
                label: `${s.storeId}${s.storeName ? ` — ${s.storeName}` : ''}${
                    s.city ? ` (${s.city})` : ''
                }`,
            }));
    }, [isZoneScoped, zones, stores, filterText]);

    const toggle = (value: string) =>
        setSelected((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value],
        );

    const added = selected.filter((v) => !initial.includes(v));
    const removed = initial.filter((v) => !selected.includes(v));
    // UM-5: removing the last value is blocked — the backend refuses it too, this just avoids a
    // round trip and states the reason up front.
    const wouldEmpty = selected.length === 0;
    const dirty = added.length > 0 || removed.length > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit access — {user?.name}
                <Typography variant="body2" color="textSecondary">
                    {roleLabel(user?.role)} ·{' '}
                    {isZoneScoped ? 'Zones' : 'Stores'}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {!isZoneScoped && (
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Search store id, name or city"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        style={{ marginBottom: 12 }}
                    />
                )}

                <Box mb={1}>
                    {selected.map((v) => (
                        <Chip
                            key={v}
                            size="small"
                            label={v}
                            onDelete={() => toggle(v)}
                            style={{ marginRight: 4, marginBottom: 4 }}
                        />
                    ))}
                </Box>

                <Box maxHeight={280} overflow="auto">
                    {options.map((opt) => (
                        <div key={opt.value}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selected.includes(opt.value)}
                                        onChange={() => toggle(opt.value)}
                                    />
                                }
                                label={opt.label}
                            />
                        </div>
                    ))}
                    {options.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            No options available.
                        </Typography>
                    )}
                </Box>

                {wouldEmpty && (
                    <Typography
                        variant="body2"
                        color="error"
                        style={{ marginTop: 8 }}
                    >
                        A user must cover at least one{' '}
                        {isZoneScoped ? 'zone' : 'store'}. Deactivate the user
                        instead.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!dirty || wouldEmpty}
                    onClick={() => onSave(user.id, added, removed)}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserScopeModal;
